import { SurveyAnswer, SurveyAttachedFile } from "@graffer-inc/cloud-gov-core";
import {
  FormDataConverter,
  FormSchema,
  InputElementClassUnion,
  InputElementTypeLiterals,
  isInputClassElement,
} from "@graffer-inc/form-schema";

export type SurveyAnswerData = {
  value: string;
  type: InputElementTypeLiterals;
  file?: SurveyAttachedFile;
};

export type SurveyAnswerTableBody = {
  answerId: string;
  answers: SurveyAnswerData[];
};

export type firstRowHeader = {
  label: string;
  rowSpan?: number;
  colSpan?: number;
};

export type secondRowHeader = {
  label: string;
};

export type SurveyAnswerTableHeader = {
  fisrtRowHeaders: firstRowHeader[];
  secondRowHeaders: secondRowHeader[];
};

export const generateSurveyAnswerTable = (
  formSchema: FormSchema,
  answers: SurveyAnswer[],
): {
  header: SurveyAnswerTableHeader;
  body: SurveyAnswerTableBody[];
} => {
  const flatten = FormSchema.fromJSON(formSchema)
    .flattenDeep()
    .filter(isInputClassElement);

  const nonDuplicateHeaders = flatten.reduce(
    (
      acc: InputElementClassUnion[],
      val: InputElementClassUnion,
    ): InputElementClassUnion[] => {
      // 繰り返し要素じゃなかったらHeaderを表示する
      if (!val.parent) return [...acc, val];

      // 繰返し項目が、まだ1つもなかったら、追加する
      if (acc.length === 0) return [...acc, val];
      const onlyRepeatable = acc.filter((v) => v.parent);
      if (onlyRepeatable.length === 0) return [...acc, val];

      // 既にある繰返し項目と、IDも繰り返し回数も同じなら、追加しない
      if (
        onlyRepeatable.some(
          (v) =>
            v.parent?.repeatable.id === val.parent?.repeatable.id &&
            v.parent?.elemIndex === val.parent?.elemIndex,
        )
      )
        return acc;

      return [...acc, val];
    },
    [],
  );

  const secondRowHeaders: secondRowHeader[] = flatten
    .filter((v) => {
      return v.parent;
    })
    .map((v) => {
      return {
        label: v.label.text,
      };
    });

  const firstRowHeaders: firstRowHeader[] = nonDuplicateHeaders.map((v) => {
    return {
      label: v.parent
        ? `${v.parent.repeatable.label.text} #${v.parent.elemIndex + 1}`
        : v.label.text,
      colSpan: v.parent ? v.parent.repeatable.baseUnit.length : undefined,
      rowSpan: secondRowHeaders.length !== 0 && !v.parent ? 2 : undefined,
    };
  });

  const header: SurveyAnswerTableHeader = {
    fisrtRowHeaders: firstRowHeaders,
    secondRowHeaders: secondRowHeaders,
  };

  const body: SurveyAnswerTableBody[] = answers.map((answer) => {
    const formDataMap = new FormDataConverter(
      formSchema,
      answer.answer,
    ).flattenWithElement();

    return {
      answerId: answer.id,
      answers: flatten.map((v) => {
        const formData = formDataMap[v.formFieldId];
        // 繰り返し項目で、回答側で繰り返しが行われなかった場合、formDataがundefinedになる
        if (!formData) {
          return {
            value: "",
            type: v.inputType,
            file: undefined,
          };
        }
        return {
          // NOTE(yo-tak): requireSelectableLabelValue をtrueにすると選択肢のlabelが返ってくる。これは requireSelectableLabelValue 導入前と同じ振る舞いである。
          // 将来、画面でもvalueを表示したいという要望が来た時に、選択可能とするかなどを検討する。
          value: formData.element.toCSVOutputValue(formData.value, {
            requireSelectableLabelValue: true,
          }),
          type: formData.element.inputType,
          file: answer.files.find((file) => file.refKey === formData.value),
        };
      }),
    };
  });

  if (answers.length !== body.length) {
    throw new Error("Answer formatting failure");
  }

  return {
    header,
    body,
  };
};
